import {
  Alert,
  Anchor,
  Button,
  Image,
  Grid,
  Stack,
  Text,
  Title,
  Container,
  Typography,
  List,
} from "@mantine/core";
import { IconCalendarOff, IconWritingSign } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { useListApplicationProcessesOpenForEnrolment } from "../hooks/useListApplicationProcessesOpenForEnrolment.ts";
import { useLoggedInUser } from "../hooks/useLoggedInUser.ts";
import { useListParticipationInApplicationProcessByCandidateId } from "../hooks/useListParticipationInApplicationProcessByCandidateId.ts";
import { dateTimeFormat } from "../layout/datetime.ts";
import { TimeZoneHint } from "../components/TimeZoneHint.tsx";
import DMXVisual from "../layout/images/mappenportal-visual-dmx.jpg";
import FachbereichVisual from "../layout/images/mappenportal-visual-fb1.jpg";
import FUBVisual from "../layout/images/mappenportal-visual-fub.jpg";
import KDVisual from "../layout/images/mappenportal-visual-kd.jpg";
import MVisual from "../layout/images/mappenportal-visual-m.jpg";

/**
 * Beantwortet die erste Frage jeder Besucherin — „kann ich mich jetzt
 * bewerben, und bis wann?" — bevor sie den Fliesstext lesen muss.
 *
 * Vorher stand die Antwort nur indirekt im Text („Anmeldefrist ist immer
 * 1. Januar oder 1. Juni", „zwei Monate vorher geoeffnet"); ausrechnen musste
 * sie jede selbst, obwohl der Server die Zeitraeume kennt. Bei einem Fehler
 * bleibt der Kasten weg statt eine falsche Auskunft zu geben — der Fliesstext
 * darunter traegt die Seite weiterhin.
 */
const EnrolmentStatus = () => {
  const offeneVerfahren = useListApplicationProcessesOpenForEnrolment();
  const loggedInUser = useLoggedInUser();
  const participations = useListParticipationInApplicationProcessByCandidateId(
    loggedInUser.data?.id,
  );

  if (!offeneVerfahren.isSuccess) {
    return null;
  }

  /*
    Wer angemeldet ist, wartet noch auf seine Bewerbungen — bis sie da sind,
    bleibt der Kasten weg. Sonst blitzte fuer einen Moment die Aufforderung auf,
    sich zu bewerben, obwohl das laengst erledigt ist.
  */
  const angemeldet = !!loggedInUser.data?.id;

  if (angemeldet && !participations.isSuccess) {
    return null;
  }

  const offeneVerfahrensIds = new Set(offeneVerfahren.data.map((a) => a.id));
  const bereitsBeworben = (participations.data ?? []).some((p) =>
    offeneVerfahrensIds.has(p.applicationProcessId),
  );

  /*
    Wer sich schon beworben hat, braucht die Aufforderung nicht mehr — der
    Stand steht unter „Meine Bewerbung", und die Kopfzeile fuehrt dorthin.
    Das betrifft auch Verwaltung und Kommission: sie haben keine Bewerbungen,
    bekommen aber ueber `angemeldet` ebenfalls keinen Registrierungsaufruf.
  */
  if (bereitsBeworben) {
    return null;
  }

  if (offeneVerfahren.data.length == 0) {
    return (
      <Alert
        color="sand"
        mb="xl"
        icon={<IconCalendarOff />}
        title="Zurzeit ist keine Bewerbung möglich"
      >
        Das Mappenportal öffnet jeweils rund zwei Monate vor der
        Eignungsprüfung. Die genauen Termine stehen{" "}
        <Anchor
          href="https://www.hsbi.de/gestaltung/studieninteressierte/ba#eignungspruefung"
          target="_blank"
        >
          auf der Info-Seite der Hochschule
        </Anchor>
        .
      </Alert>
    );
  }

  // Bei zwei parallelen Phasen zaehlt fuer die Ueberschrift die naechste Frist.
  const naechstesEnde = offeneVerfahren.data
    .map((a) => DateTime.fromISO(a.periods.enrolment.end))
    .reduce((a, b) => (a < b ? a : b));

  return (
    <>
      <Alert color="green" mb="md" title="Die Bewerbungsphase läuft">
        <Text>
          Du kannst dich noch bis{" "}
          <strong>{naechstesEnde.toFormat(dateTimeFormat)} Uhr</strong>{" "}
          bewerben.
          <TimeZoneHint dateTime={naechstesEnde} />
        </Text>
      </Alert>
      {/*
        „Registrieren und anmelden" hiess zweimal dasselbe — im Portal ist
        „anmelden" ausserdem das Einloggen. Der Knopf nennt jetzt das Ziel.

        Wer schon ein Konto hat, wird nicht zur Registrierung geschickt,
        sondern direkt zur Auswahl der Studienrichtung.
      */}
      <Button
        component={Link}
        to={angemeldet ? "/bewerbung/offene-bewerbungen" : "/registrieren"}
        color="blue"
        size="md"
        mb="xl"
        leftSection={<IconWritingSign />}
      >
        Jetzt bewerben
      </Button>
    </>
  );
};

export const Homepage = () => {
  return (
    <Container fluid>
      <Grid my="lg" gap="lg">
        <Grid.Col span={5} visibleFrom="md">
          <Stack align="flex-end" justify="flex-start" gap="xl">
            <div>
              <Image src={FachbereichVisual} alt="" h={250} w={333.3} />
            </div>
            <div>
              <Image src={MVisual} alt="" h={350} w={407.5} />
            </div>
            <div>
              <Image src={DMXVisual} alt="" h={250} w={333.3} />
            </div>
            <div>
              <Image src={KDVisual} alt="" h={150} w={200} />
            </div>
            <div>
              <Image src={FUBVisual} alt="" h={350} w={407.5} />
            </div>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ md: 5 }}>
          <Title mb="xl">
            Anmeldung zur Eignungsprüfung FB Gestaltung Bielefeld
          </Title>

          <EnrolmentStatus />

          {/*
              `Typography` ist fuer rohes HTML gedacht und faerbt jedes <a>
              darin in der Anchor-Farbe. Solange es die ganze Seite umschloss,
              galt das auch fuer einen Button, der als Link gerendert wird —
              seine Beschriftung landete bei 1,26:1. Es umschliesst deshalb nur
              noch den Fliesstext.
            */}
          <Typography>
            <Text>Liebe Bewerber.innen,</Text>
            <Text>
              wir freuen uns, dass Ihr Euch für ein Studium bei uns am
              Fachbereich Gestaltung in Bielefeld interessiert. Wir bieten Euch
              zwei Termine für die Eignungsprüfung zum Bachelor-Studiengang
              Gestaltung B.A. an. Die Anmeldefrist dafür ist immer 1. Januar
              oder 1. Juni.
            </Text>
            <Text>
              Die Bewerbung im Mappenportal gilt als Anmeldung, egal ob eine
              analoge oder digitale Mappe abgegeben wird. Das Mappenportal ist
              immer zwei Monate vorher für Euch geöffnet.
            </Text>
            {/*
              Der fruehere Fliesstext-Link „Registrieren und für die
              Eignungsprüfung anmelden" stand hier. Er ist in den Statuskasten
              oben gewandert — dort steht er neben der Frist, auf die er sich
              bezieht, und konkurriert nicht mehr mit dem Login-Knopf im Kopf.
            */}
            <Title order={2}>Die vier Studienrichtungen</Title>
            <Text>
              Erster Schritt ist die Auswahl der Studienrichtung. Unentschieden?
              Ihr könnt Euch auch für zwei Studienrichtungen anmelden, dann gebt
              jeweils eine passende Mappe ab.
            </Text>
            <Text>Informiert Euch hier über die Studieninhalte:</Text>
            <List>
              <List.Item>
                <Anchor
                  aria-label="Studieninhalte Digital Media and Experiment"
                  href="https://www.hsbi.de/gestaltung/dmx/studienrichtung"
                  target="_blank"
                >
                  Digital Media and Experiment
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Studieninhalte Fotografie und Bildmedien"
                  href="https://www.hsbi.de/gestaltung/fub/studienrichtung"
                  target="_blank"
                >
                  Fotografie und Bildmedien
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Studieninhalte Kommunikationsdesign"
                  href="https://www.hsbi.de/gestaltung/kd/studienrichtung"
                  target="_blank"
                >
                  Kommunikationsdesign
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Studieninhalte Mode"
                  href="https://www.hsbi.de/gestaltung/mode/studienrichtung"
                  target="_blank"
                >
                  Mode
                </Anchor>
              </List.Item>
            </List>
            <Title order={2}>Die Mappe</Title>
            <Text>
              Die Mappe dient der Vorauswahl. Ihr könnt sie entweder digital im
              Mappenportal abgeben, oder analog im Fachbereich. Sie wird dann
              von den Kommissionen gesichtet und Ihr erfahrt am Mittwoch vor der
              Eignungsprüfung per Mail, ob Ihr eingeladen werdet.
            </Text>
            <Text>Hier sind die Vorgaben für die Mappe:</Text>

            <List>
              <List.Item>
                <Anchor
                  aria-label="Mappenvorgaben Digital Media and Experiment"
                  href="https://www.hsbi.de/gestaltung/dmx/mappe"
                  target="_blank"
                >
                  Digital Media and Experiment
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Mappenvorgaben Fotografie und Bildmedien"
                  href="https://www.hsbi.de/gestaltung/fub/mappe"
                  target="_blank"
                >
                  Fotografie und Bildmedien
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Mappenvorgaben Kommunikationsdesign"
                  href="https://www.hsbi.de/gestaltung/kd/mappe"
                  target="_blank"
                >
                  Kommunikationsdesign
                </Anchor>
              </List.Item>
              <List.Item>
                <Anchor
                  aria-label="Mappenvorgaben Mode"
                  href="https://www.hsbi.de/gestaltung/mode/mappe"
                  target="_blank"
                >
                  Mode
                </Anchor>
              </List.Item>
            </List>

            <Title order={2}>Ablauf Eignungsprüfung und Bewerbung</Title>

            <Text>
              Auf unserer Info Page seht Ihr alle Details zum Ablauf der
              Eignungsprüfung bei uns und der späteren Bewerbung an der
              Hochschule Bielefeld.
            </Text>

            <Text>
              <Anchor href="https://www.hsbi.de/gestaltung/studieninteressierte/ba">
                Info Page Eignungsprüfung und Bewerbung
              </Anchor>
            </Text>
          </Typography>
        </Grid.Col>
      </Grid>
    </Container>
  );
};

export default Homepage;
